import type { FC, PropsWithChildren } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import type { PageDataLoadContextProviderProps } from './PageDataLoadContextProvider.types';
import type { PageDataLoadContextValue } from './context';
import { PageDataLoadContext } from './context';
import { usePreloadPageDataCallback } from './hooks';

const PageDataLoadContextProvider: FC<PropsWithChildren<PageDataLoadContextProviderProps>> = ({
  dataLoader,
  children,
}) => {
  'use memo';

  const [value, setValue] = useState<PageDataLoadContextValue>();
  const preloadPageData = usePreloadPageDataCallback(setValue);
  const { pathname, search } = useLocation();
  const routeParams = useParams();
  const hasPreloadedRef = useRef(false);

  useEffect(() => {
    if (!hasPreloadedRef.current && dataLoader) {
      const queryParams = new URLSearchParams(search);
      const resolvedParams = {
        pathname,
        ...routeParams,
        ...Object.fromEntries(queryParams.entries()),
      };

      void preloadPageData(dataLoader, resolvedParams).catch((error: unknown) => {
        setValue({
          pageLoadError: error instanceof Error ? error : new Error(`Page data loader failed: ${String(error)}`),
        });
      });
      hasPreloadedRef.current = true;
    }
  }, [dataLoader, pathname, preloadPageData, routeParams, search]);

  return <PageDataLoadContext.Provider value={value}>{children}</PageDataLoadContext.Provider>;
};

export default PageDataLoadContextProvider;
